import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { PERM } from '#/config/permissions';
import EntityStatusBadge from '#/modules/commonComponents/table/EntityStatusBadge';
import { useCan } from '#/modules/infra/auth/useCan';
import MtrCommandBar from '#/modules/imsMgmt/settings/mtr/components/MtrCommandBar';
import MtrUnitsReadonlySection from '#/modules/imsMgmt/settings/mtr/components/MtrUnitsReadonlySection';
import { parseDetailAssemblyOptions } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import {
  formatMtrKindLabel,
  formatMtrStatusLabel,
  formatOriginalSerialList,
} from '#/modules/imsMgmt/settings/mtr/helper/mtrFilterOptions';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function resolveSpecLabel(item) {
  const code = item?.mtrSpec?.code ?? item?.code;
  const name = item?.mtrSpec?.name ?? item?.name;
  const label = [code, name].filter(Boolean).join(' - ');
  return label || displayOrDash(item?.mtrSpecId ?? item?.id);
}

function resolveOptionLabel(item) {
  const option = item?.mtrSpecOption ?? item?.selectedOption ?? item?.option;
  if (!option) {
    return displayOrDash(item?.mtrSpecOptionId);
  }
  const label = [option.code, option.name, option.label].filter(Boolean).join(' - ');
  return label || displayOrDash(option.id);
}

function MtrMainInfoCard({ detail, actions = null }) {
  const status = detail?.status;
  const template = detail?.mtrTemplate;

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {displayOrDash(detail?.mtrNo)}
              </h1>
              {status ? (
                <EntityStatusBadge status={status} label={formatMtrStatusLabel(status)} />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {displayOrDash(detail?.name)}
              {' · '}
              {formatMtrKindLabel(detail?.mtrKind)}
            </p>
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
            'sm:grid-cols-2 md:grid-cols-4',
          )}
        >
          <HistoryField
            label="建立"
            value={formatDateTimeOrDash(
              detail?.record?.createdAt ?? detail?.createdAt,
            )}
          />
          <HistoryField
            label="修改"
            value={formatDateTimeOrDash(
              detail?.record?.updatedAt ?? detail?.updatedAt,
            )}
          />
          <HistoryField
            label="主分類"
            value={displayOrDash(detail?.mtrStructMain?.name)}
          />
          <HistoryField
            label="次分類"
            value={displayOrDash(detail?.mtrStructSub?.name)}
          />
          <HistoryField
            label="模板"
            value={
              template
                ? [template.code, template.name].filter(Boolean).join(' - ') || displayOrDash(template.id)
                : '—'
            }
          />
          <HistoryField
            label="製造商"
            value={displayOrDash(detail?.manufacturer?.name)}
          />
          <HistoryField
            label="基本單位"
            value={displayOrDash(
              detail?.unit?.base?.name
              || [detail?.baseUnit?.code, detail?.baseUnit?.name]
                .filter(Boolean)
                .join(' · '),
            )}
          />
          <HistoryField
            label="基礎單價"
            value={formatDisplayAmount(detail?.baseUnitPrice)}
          />
          <HistoryField
            label="安全庫存"
            value={formatDisplayQty(detail?.safetyQty)}
          />
          <div className="sm:col-span-2 md:col-span-2">
            <p className="text-xs text-gray-500">原廠料號</p>
            <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
              {formatOriginalSerialList(detail?.originalSerialList)}
            </p>
          </div>
          <div className="sm:col-span-2 md:col-span-2">
            <p className="text-xs text-gray-500">備註</p>
            <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
              {displayOrDash(detail?.remark)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

MtrMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  actions: PropTypes.node,
};

function SpecReadonlyTable({ title, headers, rows, emptyText }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-gray-600">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[24rem] text-left text-sm">
            <thead className="bg-slate-100 text-xs text-gray-600">
              <tr>
                {headers.map((header) => (
                  <th key={header} className="px-2 py-1.5 font-medium whitespace-nowrap">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-200">
                  {row.cells.map((cell, index) => (
                    <td
                      key={`${row.id}-${headers[index]}`}
                      className="px-2 py-1.5 text-gray-900"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

SpecReadonlyTable.propTypes = {
  title: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    cells: PropTypes.arrayOf(PropTypes.node).isRequired,
  })).isRequired,
  emptyText: PropTypes.string.isRequired,
};

function MtrSpecsReadonlySection({ detail }) {
  const optionRows = (detail?.specOption ?? []).map((item, index) => ({
    id: String(item?.mtrSpec?.id ?? item?.mtrSpecId ?? item?.id ?? `opt-${index}`),
    cells: [resolveSpecLabel(item), resolveOptionLabel(item)],
  }));

  const inputRows = (detail?.specInput ?? []).map((item, index) => ({
    id: String(item?.mtrSpec?.id ?? item?.mtrSpecId ?? item?.id ?? `inp-${index}`),
    cells: [resolveSpecLabel(item), displayOrDash(item?.inputValue)],
  }));

  const customRows = (detail?.specCustom ?? []).map((item, index) => ({
    id: String(item?.customKey ?? `cus-${index}`),
    cells: [displayOrDash(item?.customKey), displayOrDash(item?.inputValue)],
  }));

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface space-y-4 p-5">
        <h2 className="form-block__title">規格值</h2>
        <SpecReadonlyTable
          title="規格選項（SELECT）"
          headers={['規格', '選項']}
          rows={optionRows}
          emptyText="尚無 SELECT 規格"
        />
        <SpecReadonlyTable
          title="規格輸入（INPUT）"
          headers={['規格', '值']}
          rows={inputRows}
          emptyText="尚無 INPUT 規格"
        />
        <SpecReadonlyTable
          title="自訂規格"
          headers={['鍵', '值']}
          rows={customRows}
          emptyText="尚無自訂規格"
        />
      </div>
    </div>
  );
}

MtrSpecsReadonlySection.propTypes = {
  detail: PropTypes.object.isRequired,
};

function MtrAssemblyReadonlySection({ detail }) {
  const rows = parseDetailAssemblyOptions(detail);

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">替代子料</h2>
        {rows.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無替代子料</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[28rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 font-medium">料號</th>
                  <th className="px-2 py-1.5 font-medium">名稱</th>
                  <th className="px-2 py-1.5 font-medium">狀態</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.optionMtrId} className="border-t border-slate-200">
                    <td className="px-2 py-1.5 font-mono text-xs whitespace-nowrap">
                      {displayOrDash(row.mtrNo)}
                    </td>
                    <td className="px-2 py-1.5">{displayOrDash(row.name)}</td>
                    <td className="px-2 py-1.5">
                      {row.status ? (
                        <EntityStatusBadge
                          status={row.status}
                          label={formatMtrStatusLabel(row.status)}
                        />
                      ) : formatMtrStatusLabel(row.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

MtrAssemblyReadonlySection.propTypes = {
  detail: PropTypes.object.isRequired,
};

const MtrBlock = ({ detail, onRefetch, showActions = false }) => {
  const canReadMtr = useCan(PERM.MTR_READ);

  return (
    <div className="space-y-4">
      <MtrMainInfoCard
        detail={detail}
        actions={
          showActions ? (
            <MtrCommandBar
              key={`${detail.id}-${detail.status ?? ''}-${detail.record?.updatedAt ?? detail.updatedAt ?? ''}`}
              detail={detail}
              onRefetch={onRefetch}
            />
          ) : null
        }
      />
      {detail?.mtrKind !== 'GROUP' ? (
        <MtrUnitsReadonlySection detail={detail} enabled={canReadMtr} />
      ) : null}
      <MtrSpecsReadonlySection detail={detail} />
      {detail?.mtrKind === 'GROUP' ? (
        <MtrAssemblyReadonlySection detail={detail} />
      ) : null}
    </div>
  );
};

MtrBlock.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
  showActions: PropTypes.bool,
};

export default MtrBlock;
